import React, { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import Text from "components/atoms/Text";
import CheckboxField from "components/molecules/CheckboxField";
import { Space } from "antd";
import { networkOptions } from "../utils/options";
import TableFieldExternalPagination from "components/molecules/TableFieldExternalPagination";
import DateField from "components/molecules/DateField";
import TableMenu from "components/molecules/TableMenu";
import { autoFillOutOfNetworkCoverage } from "../utils/table";
import { useLocation } from "react-router-dom";
import { frequencyOptions } from "../utils/options";

const editForOptions = (household, children, isDue) => {
  let options = [
    {
      value: "Self",
      name: "Self",
    },
  ];
  if (household === "household" && children > 0) {
    options = options
      .concat([
        {
          value: "Spouse",
          name: "Spouse",
        },
      ])
      .concat(
        Array.from({ length: children }, (_, i) => {
          return {
            value: `Child ${i + 1}`,
            name: `Child ${i + 1}`,
          };
        }),
      );
  } else if (household === "household (with no spouse)" && children > 0) {
    options = options.concat(
      Array.from({ length: children }, (_, i) => {
        return {
          value: `Child ${i + 1}`,
          name: `Child ${i + 1}`,
        };
      }),
    );
  } else if (household === "household" && !children) {
    options.push({
      value: "Spouse",
      name: "Spouse",
    });
  }

  if (isDue) {
    options.push({
      value: "Baby",
      name: "Baby",
    });
  }

  return options;
};

const columns = (managers, customForOptions) =>
  [
    {
      title: "",
      dataIndex: "idx",
      width: 22.5,
      editable: false,
      fixed: "left",
    },
    {
      title: "Drug Name",
      dataIndex: "name",
      width: 200,
      editable: true,
      inputType: "text",
      shouldCellUpdate: () => false,
    },
    {
      title: "For",
      dataIndex: "for",
      width: 60,
      editable: true,
      inputType: "select",
      options: customForOptions,
      dropdownMatchSelectWidth: 200,
      shouldCellUpdate: () => false,
    },
    {
      title: "Cost",
      dataIndex: "cost",
      width: 100,
      editable: true,
      inputType: "currency",
      shouldCellUpdate: () => false,
    },
    {
      title: "Frequency",
      dataIndex: "frequency",
      width: 100,
      editable: true,
      inputType: "select",
      options: frequencyOptions,
      shouldCellUpdate: () => false,
    },
    ...managers.map((item) => ({
      title: `${item.name} Tier`,
      dataIndex: item.id ? `${item.id}` : `${item.idx}`,
      width: 100,
      editable: true,
      inputType: "select",
      options: [
        { value: "preventive_care", name: "Preventive Care" },
        ...item.options,
        { value: "not_covered", name: "Not Covered" },
      ],
      shouldCellUpdate: () => false,
    })),
    {
      title: "",
      key: "operation",
      fixed: "right",
      width: 48,
      render: (_, record) => <TableMenu record={record} />,
    },
  ].map((col) => {
    return {
      ...col,
      onCell: (_, idx) => ({
        index: idx,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        inputType: col.inputType,
        options: col.options,
        dropdownMatchSelectWidth: col.dropdownMatchSelectWidth,
        disabled: col.disabled || false,
        emptyValue: col.emptyValue,
      }),
    };
  });

const handleAddButton = (table, setFieldValue, tableName) => {
  const nextIndex = table.length !== 0 ? Math.max(...table.map((element) => element.idx)) + 1 : 1;
  const obj = {
    idx: Math.floor(nextIndex),
    tableName: tableName,
  };
  table.push(obj);
  setFieldValue(tableName, [...table]);
};

const Step = () => {
  const { state } = useLocation();
  const { values, setErrors, setTouched, setFieldValue } = useFormikContext();
  const { report, table, drugsTable, pharmacyManagersTable } = values;
  const {
    type: household,
    event: {
      general: { children },
    },
    isDue,
  } = report;
  const managers = pharmacyManagersTable.map((item) => ({
    idx: item.idx,
    name: item.name,
    id: item.id,
    options: Array.from({ length: item.tierCodes.length }, (_, i) => ({
      value: `${item.tierCodes[i]} - ${item.tierNames[i]}`,
      name: `${item.tierCodes[i]} - ${item.tierNames[i]}`,
    })),
  }));

  const syncedRef = useRef(false);

  useEffect(() => {
    document.getElementById("form-content").scrollTo({ top: 0, behavior: "smooth" });
    setErrors({});
    setTouched({}, false);

    if (syncedRef.current) return;
    syncedRef.current = true;

    const newTable = table.map((plan) => {
      const mgr = pharmacyManagersTable.find((m) => m.planList.includes(plan.label));
      if (!mgr) return plan;
      return {
        ...plan,
        tiers: Array.from({ length: mgr.numberOfTiers }, (_, i) => `${mgr.tierCodes[i]} - ${mgr.tierNames[i]}`),
      };
    });
    setFieldValue("table", newTable);
  }, []);

  return (
    <>
      <Text sectionTitle>Drugs</Text>
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        <TableFieldExternalPagination
          name="drugsTable"
          columns={columns(managers, editForOptions(household, children, isDue))}
          dataSource={drugsTable}
          pageSize={10}
          tableLayout="fixed"
          handleAddButton={() => handleAddButton(drugsTable, setFieldValue, "drugsTable")}
        />
      </Space>
    </>
  );
};

export default Step;
